import { Button, Checkbox, Col, Form, Input, InputNumber, Row, Select, Typography } from "antd";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import PermissionCheck from "../../Common/PermissionCheck";
import { useStandingsConfigurationService } from "~pages/StandingsConfigurations/hooks/useStandingsConfigurationService.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import HeadToHeadParams from "./Base/HeadToHeadParams";
import { merge } from "lodash-es";
import { noLeadingOrTrailingSpacesRule } from "~utils/noLeadingOrTrailingSpacesRule.ts";

const PermissionName = PermissionNames.STANDING_CONFIGURATION;
const BUILD_RULES_LIST = [
  {
    value: "ALL_STAR",
    label: "All Star",
  },
  {
    value: "PRESEASON",
    label: "Preseason",
  },
  {
    value: "REGULAR",
    label: "Regular",
  },
  {
    value: "FINAL",
    label: "Final",
  },
  {
    value: "PLAYOFF",
    label: "Playoff",
  },
  {
    value: "FRIENDLY",
    label: "Friendly",
  },
  {
    value: "DEMONSTRATION",
    label: "Demonstration",
  },
];

const DEFAULT_VALUES = {
  buildRules: {
    grouping: "NONE",
    buildType: "OVERALL",
    buildOverall: false,
    buildConferences: false,
    buildDivisions: false,
    blankStanding: false,
    includeLiveFixtures: false,
    generateLiveStandings: false,
    forceHeadToHead: false,
    decimalPlaces: 4,
  },
  configuration: {
    byeIsPlayed: false,
    byeAddedWins: false,
    forfeitWonByAddedWins: false,
    forfeitWonByAddedPlayed: false,
    forfeitGivenAddedLosses: false,
    forfeitGivenAddedPlayed: false,
    streak: "WON",
    headToHeadUseAdjustments: false,
    headToHeadSingleRound: false,
    checkSecondaryScores: false,
    secondaryScoreDrawAsResult: false,
  },
  sortingSorting: "byes",
  sortingDirection: "ASC",
  resolutionsSorting: "byes",
  resolutionsDirection: "ASC",
  identificationSorting: "byes",
};

const ManageStandingConfigurationsForm = ({ standingConfiguration = null }) => {
  const [form] = Form.useForm();
  const { formatMessage } = useIntl();
  const { standingConfigurationId } = useParams();
  const [configuration, setConfiguration] = useState({
    headToHeadIdentification: standingConfiguration?.configuration?.headToHeadIdentification,
    headToHeadResolutions: standingConfiguration?.configuration?.headToHeadResolutions,
    headToHeadIdentificationSubsequent: standingConfiguration?.configuration?.headToHeadIdentificationSubsequent,
    headToHeadResolutionsSubsequent: standingConfiguration?.configuration?.headToHeadResolutionsSubsequent,
    sorts: standingConfiguration?.configuration?.sorts,
  });
  const { createStandingsConfiguration, isCreatePending, updateStandingsConfiguration, isUpdatePending } =
    useStandingsConfigurationService();

  const handleSetConfiguration = (value) => {
    setConfiguration(value);
  };

  const handleSubmit = async () => {
    const valid = await form.validateFields();
    const data = {
      nameLatin: valid?.nameLatin,
      nameLocal: valid?.nameLocal,
      configuration: valid?.configuration,
      buildRules: valid?.buildRules,
    };

    if (Object.keys(configuration)?.length) {
      data.configuration = {
        ...data.configuration,
        ...configuration,
      };
    }

    if (standingConfigurationId) {
      await updateStandingsConfiguration(standingConfigurationId, data);
      return;
    }

    await createStandingsConfiguration(data);
  };

  const initialFormValues = merge({}, DEFAULT_VALUES, standingConfiguration);

  return (
    <>
      <Form
        scrollToFirstError
        colon={false}
        form={form}
        initialValues={initialFormValues}
        labelCol={{
          span: 14,
        }}
        wrapperCol={{
          span: 8,
        }}
        labelWrap={true}
      >
        <Row>
          <Col span={10}>
            <Form.Item
              name={"nameLocal"}
              wrapperCol={{ span: 14 }}
              label={formatMessage({
                id: "name.local.full",
                defaultMessage: "Local Full Name",
              })}
              tooltip={formatMessage(
                {
                  id: "form.help.local.full.name.item",
                  defaultMessage: "The full name of the {item} in the local Language",
                },
                {
                  item: formatMessage({
                    id: "standing.configurations",
                    defaultMessage: "Standing Configurations",
                  }),
                },
              )}
              required={true}
              rules={[
                {
                  required: true,
                  message: "Please input name",
                },
                {
                  min: 2,
                  message: formatMessage({ id: "form.validation.min.length" }, { length: 2 }),
                },
                noLeadingOrTrailingSpacesRule(formatMessage),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={"nameLatin"}
              wrapperCol={{ span: 14 }}
              label={formatMessage({
                id: "name.latin.full",
                defaultMessage: "Latin Full Name",
              })}
              tooltip={formatMessage(
                {
                  id: "form.help.latin.full.name.item",
                  defaultMessage: "The full name of the {item} in latin characters",
                },
                {
                  item: formatMessage({
                    id: "standing.configurations",
                    defaultMessage: "Standing Configurations",
                  }),
                },
              )}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Typography.Title level={5}>
          {formatMessage({
            id: "build.rules",
            defaultMessage: "Build Rules",
          })}
        </Typography.Title>
        <Row>
          <Col span={5}>
            <Row>
              <Col span={24}>
                <Form.Item
                  required={true}
                  rules={[
                    {
                      required: true,
                      message: "Please choose fixture type",
                    },
                  ]}
                  name={["buildRules", "fixtureTypes"]}
                  label={formatMessage({
                    id: "fixture.types",
                    defaultMessage: "Fixture Types",
                  })}
                  tooltip={formatMessage({
                    id: "form.help.standing.configurations.fixture.types",
                    defaultMessage: "Which types of fixtures do you want to contrinbute towards this standings?",
                  })}
                >
                  <Checkbox.Group
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                    options={BUILD_RULES_LIST}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={18}>
            <Row>
              <Col span={8}>
                <Form.Item
                  required={true}
                  rules={[
                    {
                      required: true,
                      message: "Please choose build type",
                    },
                  ]}
                  name={["buildRules", "buildType"]}
                  label={formatMessage({
                    id: "build.rules.build.type",
                    defaultMessage: "Build Type",
                  })}
                  tooltip={formatMessage({
                    id: "form.help.standing.configurations.build.type",
                    defaultMessage: "Overall standing type",
                  })}
                >
                  <Select popupMatchSelectWidth={false}>
                    <Select.Option value={"ROUND"}>Round</Select.Option>
                    <Select.Option value={"OVERALL"}>Overall</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  required={true}
                  rules={[
                    {
                      required: true,
                      message: "Please choose build grouping",
                    },
                  ]}
                  name={["buildRules", "grouping"]}
                  label={formatMessage({
                    id: "build.rules.grouping",
                    defaultMessage: "Grouping",
                  })}
                  tooltip={formatMessage({
                    id: "form.help.standing.configurations.grouping",
                    defaultMessage: "How do you want to group this standings?",
                  })}
                >
                  <Select popupMatchSelectWidth={false}>
                    <Select.Option value={"NONE"}>None</Select.Option>
                    <Select.Option value={"STAGE"}>Stage</Select.Option>
                    <Select.Option value={"STAGEPOOL"}>Stage & Pool</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={["buildRules", "buildOverall"]}
                  label={formatMessage({
                    id: "build.rules.build.overall",
                    defaultMessage: "Build Overall",
                  })}
                  tooltip={formatMessage({
                    id: "form.help.standing.configurations.build.overall",
                    defaultMessage: "Build overall standings",
                  })}
                >
                  <Select>
                    <Select.Option value={false}>No</Select.Option>
                    <Select.Option value={true}>Yes</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={["buildRules", "buildConferences"]}
                  label={formatMessage({
                    id: "build.rules.build.conferences",
                    defaultMessage: "Build Conferences",
                  })}
                  tooltip={formatMessage({
                    id: "form.help.standing.configurations.build.conferences",
                    defaultMessage: "Build standings by conference",
                  })}
                >
                  <Select>
                    <Select.Option value={false}>No</Select.Option>
                    <Select.Option value={true}>Yes</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={["buildRules", "buildDivisions"]}
                  label={formatMessage({
                    id: "build.rules.build.divisions",
                    defaultMessage: "Build Divisions",
                  })}
                  tooltip={formatMessage({
                    id: "form.help.standing.configurations.build.divisions",
                    defaultMessage: "Build standings by division",
                  })}
                >
                  <Select>
                    <Select.Option value={false}>No</Select.Option>
                    <Select.Option value={true}>Yes</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={["buildRules", "blankStanding"]}
                  label={formatMessage({
                    id: "blankStanding",
                    defaultMessage: "Blank Standing",
                  })}
                  tooltip={formatMessage({
                    id: "form.help.standing.configurations.build.blank",
                    defaultMessage: "Give team blank row if no played",
                  })}
                >
                  <Select>
                    <Select.Option value={false}>No</Select.Option>
                    <Select.Option value={true}>Yes</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={["buildRules", "includeLiveFixtures"]}
                  label={formatMessage({
                    id: "include.live.fixtures",
                    defaultMessage: "Include Live Fixtures",
                  })}
                  tooltip={formatMessage({
                    id: "include.live.matches",
                    defaultMessage: "Include live matches",
                  })}
                >
                  <Select>
                    <Select.Option value={false}>No</Select.Option>
                    <Select.Option value={true}>Yes</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={["buildRules", "generateLiveStandings"]}
                  label={formatMessage({
                    id: "generate.live.standings",
                    defaultMessage: "Generate Live Standings",
                  })}
                  tooltip={formatMessage({
                    id: "generate.only.live.matches",
                    defaultMessage: "Generate only live matches",
                  })}
                >
                  <Select>
                    <Select.Option value={false}>No</Select.Option>
                    <Select.Option value={true}>Yes</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={["buildRules", "decimalPlaces"]}
                  label={formatMessage({
                    id: "decimal.places",
                    defaultMessage: "Decimal Places",
                  })}
                  tooltip={formatMessage({
                    id: "form.help.standing.configurations.decimal.places",
                    defaultMessage: "Number of decimal places for percentage calculations",
                  })}
                >
                  <InputNumber style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={["buildRules", "forceHeadToHead"]}
                  label={formatMessage({
                    id: "force.head.to.head",
                  })}
                  tooltip={formatMessage({
                    id: "form.help.standing.configurations.force.head.to.head",
                  })}
                >
                  <Select>
                    <Select.Option value={false}>No</Select.Option>
                    <Select.Option value={true}>Yes</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>

        <Typography.Title level={5}>
          {formatMessage({
            id: "configuration",
            defaultMessage: "Configuration",
          })}
        </Typography.Title>

        <Row>
          <Col span={8}>
            <Form.Item
              name={["configuration", "winsHomePoints"]}
              label={formatMessage({
                id: "wins.home.points",
                defaultMessage: "Points for Home Win",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.points.for.win.at.home",
                defaultMessage: "Number of Points Awarded for Home Win",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "lossesHomePoints"]}
              label={formatMessage({
                id: "losses.home.points",
                defaultMessage: "Points for Home Loss",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.points.for.losing.at.home",
                defaultMessage: "Number of Points Awarded for Home Loss",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "drawsHomeScoredPoints"]}
              label={formatMessage({
                id: "draws.home.scored.points",
                defaultMessage: "Draw Home Scored Points",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.points.given.none.zero.draw.at.home",
                defaultMessage: "Number of Points Awarded for Home Draw where team scored",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "drawsHomeZeroPoints"]}
              label={formatMessage({
                id: "draws.home.zero.points",
                defaultMessage: "Draw Home Zero Points",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.points.given.zero.draw.at.home",
                defaultMessage: "Number of Points Awarded for Home Draw with zero score",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "forfeitWonByHomePoints"]}
              label={formatMessage({
                id: "wins.home.forfeit",
                defaultMessage: "Points for win by Forfeit at home",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.win.at.home.by.forfeit",
                defaultMessage: "Number of Points Awarded for winning by Forfeit at home",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "forfeitGivenHomePoints"]}
              label={formatMessage({
                id: "forfeit.given.home.points",
                defaultMessage: "Points for giving a forfeit at home",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.standing.points.for.forfeiting.home.game",
                defaultMessage: "Number of Points Awarded for giving a forfeit at home",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "winsAwayPoints"]}
              label={formatMessage({
                id: "wins.away.points",
                defaultMessage: "Points for Away Win",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.standing.points.for.win.away",
                defaultMessage: "Number of Points Awarded for Away Win",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "lossesAwayPoints"]}
              label={formatMessage({
                id: "losses.away.points",
                defaultMessage: "Points for Away Loss",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.points.for.losing.away",
                defaultMessage: "Number of Points Awarded for Away Loss",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "drawsAwayScoredPoints"]}
              label={formatMessage({
                id: "draws.away.scored.points",
                defaultMessage: "Points for Away Draw where entity scored",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.points.given.none.zero.score.draw.away",
                defaultMessage: "Number of Points Awarded for Away Draw where team scored",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "drawsAwayZeroPoints"]}
              label={formatMessage({
                id: "draws.away.zero.points",
                defaultMessage: "Points for Away Draw with zero score",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.points.given.zero.draw.away",
                defaultMessage: "Number of Points Awarded for Away Draw with zero score",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "forfeitWonByAwayPoints"]}
              label={formatMessage({
                id: "forfeit.wins.by.away.points",
                defaultMessage: "Points for wins by Forfeit at away",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.standing.points.for.win.forfeit.away",
                defaultMessage: "Number of Points Awarded for won by Forfeit at away",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "forfeitGivenAwayPoints"]}
              label={formatMessage({
                id: "forfeit.given.away.points",
                defaultMessage: "Points for giving a forfeit at away",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.standing.points.for.forfeiting.away.game",
                defaultMessage: "Number of Points Awarded for giving a forfeit at away",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "byePoints"]}
              label={formatMessage({
                id: "bye.points",
                defaultMessage: "Points for bye",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.standing.points.bye",
                defaultMessage: "Number of Points Awarded for bye",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name={["configuration", "byeIsPlayed"]}
              label={formatMessage({
                id: "bye.is.played",
                defaultMessage: "Is a Bye added to played ?",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.is.bye.played",
                defaultMessage: "Is a Bye added to played?",
              })}
            >
              <Select>
                <Select.Option value={false}>No</Select.Option>
                <Select.Option value={true}>Yes</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "byeAddedWins"]}
              label={formatMessage({
                id: "bye.added.wins",
                defaultMessage: "Are Bye wins added to Total wins?",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.is.bye.wins.added.to.total.wins",
                defaultMessage: "Is a Bye added to won count?",
              })}
            >
              <Select>
                <Select.Option value={false}>No</Select.Option>
                <Select.Option value={true}>Yes</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "forfeitWonByAddedWins"]}
              label={formatMessage({
                id: "forfeit.wins.by.added.wins",
                defaultMessage: "Is a forfeit wins by (received) included in wins count?",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.is.bye.wins.added.to.total.wins",
                defaultMessage: "Is a forfeit won by (received) included in won count?",
              })}
            >
              <Select>
                <Select.Option value={false}>No</Select.Option>
                <Select.Option value={true}>Yes</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "forfeitWonByAddedPlayed"]}
              label={formatMessage({
                id: "forfeit.wins.by.added.played",
                defaultMessage: "Is a forfeit wins by (received) included in played count?",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.won.by.forfeit.add.to.played",
                defaultMessage: "Is a forfeit won by (received) included in played count?",
              })}
            >
              <Select>
                <Select.Option value={false}>No</Select.Option>
                <Select.Option value={true}>Yes</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "forfeitGivenAddedLosses"]}
              label={formatMessage({
                id: "forfeit.given.added.losses",
                defaultMessage: "Is a forfeit given included in losses count?",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.forfeit.given.add.to.losses",
                defaultMessage: "Is a forfeit given included in losses count?",
              })}
            >
              <Select>
                <Select.Option value={false}>No</Select.Option>
                <Select.Option value={true}>Yes</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "forfeitGivenAddedPlayed"]}
              label={formatMessage({
                id: "forfeit.given.added.played",
                defaultMessage: "Is a forfeit given included in played count?",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.forfeit.given.add.to.played",
                defaultMessage: "Is a forfeit given included in played count?",
              })}
            >
              <Select>
                <Select.Option value={false}>No</Select.Option>
                <Select.Option value={true}>Yes</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "streak"]}
              label={formatMessage({
                id: "streak",
                defaultMessage: "How is streak calculcated?",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.winning.streak.for.competitor",
                defaultMessage: "How is a streak calculated?",
              })}
            >
              <Select popupMatchSelectWidth={false}>
                <Select.Option value={"WON"}>
                  {formatMessage({
                    id: "wins",
                    defaultMessage: "WON",
                  })}
                </Select.Option>
                <Select.Option value={"NONLOST"}>
                  {formatMessage({
                    id: "nonlosses",
                    defaultMessage: "nonlosses",
                  })}
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "headToHeadUseAdjustments"]}
              label={formatMessage({
                id: "head.to.head.use.adjustments",
                defaultMessage: "Apply Standing Adjustments when performing Head to head resolutions?",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.head.to.head.use.adjustments",
                defaultMessage: "Apply Standing Adjustments when performing Head to head resolutions?",
              })}
            >
              <Select>
                <Select.Option value={false}>No</Select.Option>
                <Select.Option value={true}>Yes</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "headToHeadSingleRound"]}
              label={formatMessage({
                id: "head.to.head.single.round",
                defaultMessage: "Only use single round for Head to head resolutions?",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.head.to.head.single.round",
                defaultMessage: "Only use single round for Head to head resolutions?",
              })}
            >
              <Select>
                <Select.Option value={false}>No</Select.Option>
                <Select.Option value={true}>Yes</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "checkSecondaryScores"]}
              label={formatMessage({
                id: "secondary.scores.for.calculation",
                defaultMessage: "Secondary Score For Calculation",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.check.secondary.scores",
                defaultMessage: "Check Secondary Score for calculations?",
              })}
            >
              <Select>
                <Select.Option value={false}>No</Select.Option>
                <Select.Option value={true}>Yes</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "secondaryScoreDrawAsResult"]}
              label={formatMessage({
                id: "secondary.score.draw.as.result",
                defaultMessage: "Secondary Score Draw As Result",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.secondary.score.draw.as.result",
                defaultMessage: "Is a secondary Score result a game result (resultPlace was a Draw)?",
              })}
            >
              <Select>
                <Select.Option value={false}>No</Select.Option>
                <Select.Option value={true}>Yes</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "lossesSecondaryScoreHomePoints"]}
              label={formatMessage({
                id: "losses.secondary.score.home.points",
                defaultMessage: "Losses Secondary Score Home Points",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.losses.secondary.score.home.points",
                defaultMessage:
                  "How many points will be losses if a home team losses a match by shootout after a draw in normal time?",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "lossesSecondaryScoreAwayPoints"]}
              label={formatMessage({
                id: "losses.secondary.score.away.points",
                defaultMessage: "Losses Secondary Score Away Points",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.losses.secondary.score.away.points",
                defaultMessage:
                  "How many points will be losses if a away team losses a match by shootout after a draw in normal time?",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "winsSecondaryScoreHomePoints"]}
              label={formatMessage({
                id: "wins.secondary.score.home.points",
                defaultMessage: "Wins Secondary Score Home Points",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.wins.secondary.score.home.points",
                defaultMessage:
                  "How many points will be awarded if a home team wins a match by shootout after a draw in normal time?",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={["configuration", "winsSecondaryScoreAwayPoints"]}
              label={formatMessage({
                id: "wins.secondary.score.away.points",
                defaultMessage: "Wins Secondary Score Away Points",
              })}
              tooltip={formatMessage({
                id: "form.help.standing.configurations.wins.secondary.score.away.points",
                defaultMessage:
                  "How many points will be awarded if a away team wins a match by shootout after a draw in normal time?",
              })}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
        <HeadToHeadParams form={form} handleSetConfiguration={handleSetConfiguration} configuration={configuration} />
        <Row justify={"center"} style={{ margin: "16px 0"}}>
          <Col>
            {!standingConfiguration ? (
              <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
                <Button className={"text-uppercase"} loading={isCreatePending} onClick={handleSubmit} type={"primary"}>
                  {formatMessage({
                    id: "add",
                    defaultMessage: "ADD",
                  })}
                </Button>
              </PermissionCheck>
            ) : (
              <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
                <Button className={"text-uppercase"} loading={isUpdatePending} onClick={handleSubmit} type={"primary"}>
                  {formatMessage({
                    id: "update",
                    defaultMessage: "UPDATE",
                  })}
                </Button>
              </PermissionCheck>
            )}
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default ManageStandingConfigurationsForm;
